import { Box, Typography } from "@mui/material";
import CheckCircleOutlineOutlined from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import React from "react";
import { tokens } from "../../../theme";
import { tints } from "../../../theme/overrides";

export type InlineAlertKind = "error" | "caution" | "success";

interface InlineAlertProps {
  kind?: InlineAlertKind;
  children: React.ReactNode;
}

const KINDS = {
  error: {
    Icon: ErrorOutlineOutlined,
    fg: () => tokens.color.negative.text,
    bg: () => tokens.color.negative.bg
  },
  caution: {
    Icon: WarningAmberOutlined,
    fg: () => tokens.color.caution.text,
    bg: () => tints.cautionPanel
  },
  success: {
    Icon: CheckCircleOutlineOutlined,
    fg: () => tokens.color.positive.text,
    bg: () => tokens.color.positive.bg
  }
} as const;

/**
 * Inline message panel that sits in the flow of a form.
 *
 * Replaces the loose red <Typography> the auth screens used: a bare coloured
 * string is easy to miss and carries no icon, so a failed login read as decoration.
 * Colours come from the `.text` tokens, which are the AA-safe pair — `.main` is
 * a fill and must never sit under a word.
 */
const InlineAlert = ({ kind = "error", children }: InlineAlertProps) => {
  const { Icon, fg, bg } = KINDS[kind];

  return (
    <Box
      role={kind === "error" ? "alert" : "status"}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        p: "11px 13px",
        borderRadius: `${tokens.radius.control}px`,
        bgcolor: bg()
      }}
    >
      <Icon sx={{ fontSize: 18, color: fg(), flex: "none", mt: "1px" }} />
      <Typography sx={{ fontSize: tokens.typography.body, color: fg(), lineHeight: 1.45 }}>
        {children}
      </Typography>
    </Box>
  );
};

export default InlineAlert;
